import * as React from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { DateRangeSchema, useCustomSearchParams } from 'common/hooks/customSearchParams';
import { DEFAULT_DATE_RANGE_PARAM, WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import _ from 'lodash';
import moment from 'moment';
import { FilterButton } from '../../../../../components/ContainerFilters/FilterButton';
import { DateInput } from '../../../../../components/ContainerFilters/DateInput';

type Date = moment.Moment | null;
export interface FilterStates {
  today: boolean;
  tomorrow: boolean;
  exactDate: Date;
  startDate: Date;
  endDate: Date;
}

export const LastFreeDayFilter: React.FC = () => {
  const controlLabelClasses = {
    label: 'font-medium text-sm py-1',
    root: 'm-0 hover:bg-blue-200 rounded-sm',
  };
  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS, true);
  const [filterStates, setFilterStates] = React.useState<FilterStates>({
    today: false,
    tomorrow: false,
    exactDate: null,
    startDate: null,
    endDate: null,
  });

  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const { lastFreeDayAt } = searchParams;
    setFilterStates({
      today: lastFreeDayAt.exactDate === 'today' || lastFreeDayAt.startDate === 'today',
      tomorrow: lastFreeDayAt.exactDate === 'tomorrow' || lastFreeDayAt.endDate === 'tomorrow',
      exactDate:
        lastFreeDayAt.exactDate && !['today', 'tomorrow'].includes(lastFreeDayAt.exactDate)
          ? moment(lastFreeDayAt.exactDate)
          : null,
      startDate:
        (lastFreeDayAt.startDate &&
          !['today', 'tomorrow'].includes(lastFreeDayAt.startDate) &&
          moment(lastFreeDayAt.startDate)) ||
        null,
      endDate:
        (lastFreeDayAt.endDate &&
          !['today', 'tomorrow'].includes(lastFreeDayAt.endDate) &&
          moment(lastFreeDayAt.endDate)) ||
        null,
    });
  }, [searchParams]);

  const clearFilters = () => {
    setSearchParams({
      lastFreeDayAt: DEFAULT_DATE_RANGE_PARAM,
    });
  };

  const handleChange = (v: 'today' | 'tomorrow') => {
    const newFilterState = filterStates;

    newFilterState[v] = !newFilterState[v];

    const defaultSearchParams = {
      importStatus: _.uniq(_.concat(searchParams.importStatus, ['yard', 'rail', 'unknown'])),
    };

    if (newFilterState.today && newFilterState.tomorrow) {
      setSearchParams({
        ...defaultSearchParams,
        lastFreeDayAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          startDate: 'today',
          endDate: 'tomorrow',
        },
      });
    } else if (newFilterState.today) {
      setSearchParams({
        ...defaultSearchParams,
        lastFreeDayAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          exactDate: 'today',
        },
      });
    } else if (newFilterState.tomorrow) {
      setSearchParams({
        ...defaultSearchParams,
        lastFreeDayAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          exactDate: 'tomorrow',
        },
      });
    } else {
      clearFilters();
    }
  };

  const handleDateChange = (date: Date, kind: 'start' | 'end') => {
    const defaultSearchParams = {
      importStatus: _.uniq(_.concat(searchParams.importStatus, ['yard', 'rail', 'unknown'])),
    };

    let startDate = filterStates.startDate || null;
    let endDate = filterStates.endDate || null;

    switch (kind) {
      case 'start':
        if (date && endDate && date.isAfter(endDate)) {
          endDate = null;
        }
        startDate = date;
        break;
      case 'end':
        if (date && startDate && date.isBefore(startDate)) {
          startDate = null;
        }
        endDate = date;
        break;
      default:
        break;
    }

    const lastFreeDayAt: DateRangeSchema = { ...DEFAULT_DATE_RANGE_PARAM, startDate, endDate };

    setSearchParams({
      ...defaultSearchParams,
      lastFreeDayAt,
    });

    return true;
  };

  return (
    <FilterButton
      title="Last free day"
      count={_.keys(_.pickBy(filterStates)).length}
      clearFilter={clearFilters}
      isOpen={open}
      onRequestOpen={setOpen}
    >
      <FormGroup className="px-2">
        <FormControlLabel
          classes={controlLabelClasses}
          control={
            <Checkbox
              size="small"
              checked={filterStates.today}
              onChange={() => handleChange('today')}
            />
          }
          label="Today"
        />
        <FormControlLabel
          classes={controlLabelClasses}
          control={
            <Checkbox
              size="small"
              checked={filterStates.tomorrow}
              onChange={() => handleChange('tomorrow')}
            />
          }
          label="Tomorrow"
        />
        <DateInput
          label="Start Date"
          date={filterStates.startDate}
          onChange={(date) => handleDateChange(date, 'start')}
          checked={!!filterStates.startDate}
          size="small"
        />
        <DateInput
          label="End Date"
          date={filterStates.endDate}
          onChange={(date) => handleDateChange(date, 'end')}
          checked={!!filterStates.endDate}
          size="small"
        />
      </FormGroup>
    </FilterButton>
  );
};
LastFreeDayFilter.displayName = 'TrackAndTrace.Controls.filters.LastFreeDayFilter';
